/**
 * =============================================================================
 * AI Chat Utilities - Consolidated
 * =============================================================================
 *
 * This file consolidates utilities from the following source files:
 * 1. message-processing.js (366 lines)
 * 2. dataSanitizer.js (243 lines)
 * 3. utils.js (221 lines) - original utilities
 * 4. todoUtils.js (219 lines)
 * 5. citationParser.js (210 lines)
 * 6. fileProcessing.js (174 lines)
 * 7. formatting.js (161 lines)
 * 8. slideMetadata.js (104 lines)
 * 9. outlineHelpers.js (86 lines)
 * 10. guardState.js (79 lines)
 *
 * Total: ~1,863 lines consolidated into organized sections
 */

import { buildTimerComponent, componentUpdaters } from './streamProcessor';
import { buildFunctionUrl } from '../../../shared/utils';

// =============================================================================
// SECTION 1: MESSAGE PROCESSING
// =============================================================================
// Original Files: messageSerializer.js, augmentMessageComponents.js

/**
 * Utilities for serializing and managing chat messages
 */

/**
 * Create a message object from stream state
 * @param {Object} state - Stream state
 * @param {string} state.aiMessageId - Message ID
 * @param {Array} state.components - Message components
 * @param {Object} state.metadata - Message metadata
 * @returns {Object} Serialized message object
 */
export const createMessage = (state) => {
  const baseComponents = Array.isArray(state.components)
    ? state.components.filter(component => component?.type !== 'timer')
    : [];

  const augmentedComponents = augmentMessageComponents({
    components: baseComponents,
    metadata: state.metadata
  });

  const timerComponent = buildTimerComponent(state);

  const components = timerComponent
    ? [timerComponent, ...augmentedComponents]
    : augmentedComponents;

  const metadataSnapshot = state.metadata
    ? JSON.parse(JSON.stringify(state.metadata))
    : undefined;

  return {
    id: state.aiMessageId,
    sender: 'ai',
    timestamp: new Date().toISOString(),
    components,
    metadata: metadataSnapshot,
    isStreaming: state.metadata?.isStreaming ?? true
  };
};

/**
 * Check if a message should be saved based on state changes
 * @param {Object} state - Current state
 * @param {Object} prevState - Previous state
 * @returns {boolean} True if message should be saved
 */
export const shouldSaveMessage = (state, prevState) => {
  // Quick checks first
  if (!prevState) return true;
  if (state.components.length !== prevState.components.length) return true;

  // For thinking updates, only save if content changed significantly
  if (state.components.length > 0 && state.components[0].type === 'thinking') {
    const currThinking = state.components[0];
    const prevThinking = prevState.components[0];

    if (!prevThinking || prevThinking.type !== 'thinking') return true;

    return (
      currThinking.content !== prevThinking.content ||
      currThinking.isComplete !== prevThinking.isComplete ||
      currThinking.isLoading !== prevThinking.isLoading
    );
  }

  // For other updates, do a more thorough check
  // But avoid JSON.stringify for performance
  return true; // Conservative: save other changes
};

/**
 * Check if stream is complete based on state
 * @param {Object} state - Stream state
 * @returns {boolean} True if stream is complete
 */
export const isStreamComplete = (state) => {
  if (!state || !state.metadata) {
    return false;
  }
  if (state.metadata.isComplete) {
    return true;
  }
  if (state.metadata.isStreaming === false) {
    return true;
  }
  return false;
};

/**
 * Create a user message object
 * @param {string} content - Message content
 * @param {string} messageId - Optional message ID
 * @param {Array} attachments - Optional attachments
 * @returns {Object} User message object
 */
export const createUserMessage = (content, messageId = null, attachments = []) => ({
  id: messageId || `user-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
  sender: 'user',
  timestamp: new Date().toISOString(),
  content,
  attachments: attachments || [],
  isStreaming: false
});

/**
 * Generate a unique message ID
 * @returns {string} Unique message ID
 */
export const generateMessageId = () => `msg-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

/**
 * Check if components array has specific component type(s)
 * @param {Array} components - Components array
 * @param {string|Array} types - Component type(s) to check for
 * @returns {boolean} True if component type exists
 */
const hasComponentType = (components, types) => {
  const typeSet = Array.isArray(types) ? new Set(types) : new Set([types]);
  return Array.isArray(components) && components.some(component => typeSet.has(component?.type));
};

/**
 * Augment message components with metadata and fallback content
 * @param {Object} options - Options object
 * @param {Array} options.components - Message components
 * @param {Object} options.metadata - Message metadata
 * @param {string} options.fallbackText - Fallback text if no components
 * @returns {Array} Augmented components array
 */
export const augmentMessageComponents = ({
  components,
  metadata,
  fallbackText,
} = {}) => {
  const nextMetadata = metadata || {};
  let nextComponents = Array.isArray(components) ? [...components] : [];

  // SIMPLIFIED: Just handle clarification prompts
  nextComponents = nextComponents.map((component) => {
    if (component?.type !== 'clarificationPrompt') {
      return component;
    }

    const data = component.data || {};
    const clarificationId = component.id || data.id || `clarification-${Date.now()}`;
    return {
      type: 'planDecision',
      decisionType: 'clarification',
      clarificationId,
      prompt: data.prompt || data.question || '',
      isStreaming: component.isStreaming || false,
      decision: {
        type: 'clarification',
        id: clarificationId,
        prompt: data.prompt || data.question || '',
        required: data.required !== false,
        options: Array.isArray(data.options) ? data.options : [],
        issuedAt: data.issuedAt || component.timestamp || new Date().toISOString(),
        metadata: data.metadata || null,
        context: data.context || null
      },
      timestamp: component.timestamp || new Date().toISOString()
    };
  });

  if (nextMetadata.loop?.steps && Array.isArray(nextMetadata.loop.steps) && nextMetadata.loop.steps.length > 0) {
    const hasLoopTimeline = hasComponentType(nextComponents, 'loopTimeline');
    if (!hasLoopTimeline) {
      nextComponents = componentUpdaters.updateLoopTimelineComponent(nextComponents, {
        steps: nextMetadata.loop.steps,
        summary: nextMetadata.loop.summary || null
      });
    }
  }

  // SIMPLIFIED: Plan components are self-contained, no complex restoration needed
  // Just restore from metadata if no plan component exists
  const planMeta = nextMetadata.plan;
  if (planMeta && !hasComponentType(nextComponents, 'plan')) {
    const content = planMeta.content || planMeta.fullContent || '';
    if (content) {
      nextComponents.push({
        type: 'plan',
        content,
        status: planMeta.status || 'Ready',
        showButtons: planMeta.showButtons === true,
        isStreaming: false,
        prompt: planMeta.prompt || 'Ready to implement this plan?',
        timestamp: new Date().toISOString()
      });
    }
  }

  if (Array.isArray(nextMetadata.clarifications) && nextMetadata.clarifications.length > 0) {
    const existingClarificationIds = new Set(
      nextComponents
        .filter(component => component?.type === 'planDecision' && (component.decisionType === 'clarification' || component.decision?.type === 'clarification'))
        .map(component => component.clarificationId || component.decision?.id)
        .filter(Boolean)
    );

    nextMetadata.clarifications.forEach((clarification, index) => {
      const clarificationId = clarification.id || `clarification-${index}`;
      if (existingClarificationIds.has(clarificationId)) {
        return;
      }

      const decision = {
        type: 'clarification',
        id: clarificationId,
        prompt: clarification.prompt || clarification.question || '',
        required: clarification.required !== false,
        options: Array.isArray(clarification.options) ? clarification.options : [],
        issuedAt: clarification.issuedAt || clarification.timestamp || new Date().toISOString(),
        metadata: clarification.metadata || null,
        context: clarification.context || null
      };

      nextComponents.push({
        type: 'planDecision',
        decisionType: 'clarification',
        clarificationId,
        prompt: decision.prompt,
        isStreaming: false,
        decision,
        data: { decision },
        timestamp: decision.issuedAt
      });

      existingClarificationIds.add(clarificationId);
    });
  }

  const ensureSlideCreationComponent = () => {
    if (hasComponentType(nextComponents, 'createSlides')) {
      return;
    }

    const createdSlides = Array.isArray(nextMetadata.createdSlides) ? nextMetadata.createdSlides : [];
    if (createdSlides.length === 0) {
      return;
    }

    const slideIds = Array.isArray(nextMetadata.createdSlideIds)
      ? nextMetadata.createdSlideIds.filter((id) => typeof id === 'string' && id.length > 0)
      : createdSlides
        .map((slide) => slide.id)
        .filter((id) => typeof id === 'string' && id.length > 0);

    const slideIndices = Array.isArray(nextMetadata.createdSlideIndices)
      ? nextMetadata.createdSlideIndices.filter((idx) => Number.isFinite(idx))
      : createdSlides
        .map((slide) => (Number.isFinite(slide.index) ? slide.index : null))
        .filter((idx) => Number.isFinite(idx));

    const estimatedCount = Number.isFinite(nextMetadata.slideCount)
      ? nextMetadata.slideCount
      : createdSlides.length;

    nextComponents = componentUpdaters.updateSlideCreationComponent(
      nextComponents,
      nextMetadata.createdStatus || 'Slides added',
      estimatedCount,
      false,
      null,
      slideIds,
      slideIndices,
      createdSlides.length,
      [],
      null  // No toolId - metadata augmentation
    );
  };

  const ensureSlideEditComponent = () => {
    if (hasComponentType(nextComponents, 'editSlides')) {
      return;
    }

    const editedSlides = Array.isArray(nextMetadata.editedSlides) ? nextMetadata.editedSlides : [];
    if (editedSlides.length === 0) {
      return;
    }

    const normalizedEdits = editedSlides.map((slide, index) => {
      const indexValue = Number.isFinite(slide.index)
        ? slide.index
        : (Number.isFinite(slide.slideIndex) ? slide.slideIndex : null);

      const slideNumber = Number.isFinite(slide.slideNumber)
        ? slide.slideNumber
        : (Number.isFinite(indexValue) ? indexValue + 1 : index + 1);

      return {
        slideId: slide.id || slide.slideId || null,
        slideNumber,
        title: slide.title || slide.slideTitle || `Slide ${slideNumber}`,
        index: indexValue
      };
    });

    const slideNumbers = normalizedEdits
      .map((slide) => (Number.isFinite(slide.slideNumber) ? slide.slideNumber : null))
      .filter((num) => Number.isFinite(num));

    const slideIndices = normalizedEdits
      .map((slide) => (Number.isFinite(slide.index) ? slide.index : null))
      .filter((idx) => Number.isFinite(idx));

    nextComponents = componentUpdaters.updateSlideEditComponent(
      nextComponents,
      nextMetadata.editStatus || 'Slides updated',
      slideNumbers,
      false,
      normalizedEdits,
      normalizedEdits.length,
      normalizedEdits.length,
      null  // No toolId - metadata augmentation
    );
  };

  ensureSlideCreationComponent();
  ensureSlideEditComponent();

  const researchMeta = nextMetadata.research;
  const hasResearchComponent = hasComponentType(nextComponents, 'research');
  const hasResearchData = researchMeta && (
    (Array.isArray(researchMeta.citations) && researchMeta.citations.length > 0) ||
    (typeof researchMeta.report === 'string' && researchMeta.report.trim().length > 0)
  );

  if (!hasResearchComponent && hasResearchData) {
    nextComponents = [
      ...nextComponents,
      {
        type: 'research',
        data: {
          query: researchMeta.query || 'Research',
          citations: Array.isArray(researchMeta.citations) ? researchMeta.citations : [],
          report: typeof researchMeta.report === 'string' ? researchMeta.report : null,
          sourceSummaries: Array.isArray(researchMeta.sourceSummaries) ? researchMeta.sourceSummaries : undefined,
          isLoading: researchMeta.isStreaming ?? false,
          status: researchMeta.status || null
        }
      }
    ];
  }

  const hasTextComponent = hasComponentType(nextComponents, 'text');
  if (!hasTextComponent && typeof fallbackText === 'string' && fallbackText.trim().length > 0) {
    nextComponents = [
      ...nextComponents,
      {
        type: 'text',
        content: fallbackText
      }
    ];
  }

  return nextComponents;
};

// =============================================================================
// SECTION 2: DATA SANITIZATION
// =============================================================================

/**
 * Data sanitization utilities to prevent DOM and React contamination in the data layer
 */

/**
 * Remove React fiber nodes and other non-serializable properties from HTML strings
 * @param {string} html - HTML string that might contain React internal properties
 * @returns {string} Clean HTML string
 */
export const sanitizeHTML = (html) => {
  if (typeof html !== 'string') return '';

  // Create a temporary container to parse HTML
  const temp = document.createElement('div');
  temp.innerHTML = html;

  // Remove React internal properties from all elements
  const allElements = temp.querySelectorAll('*');
  allElements.forEach(element => {
    // Remove React fiber properties
    const propsToRemove = [];
    for (const prop in element) {
      if (prop.startsWith('__react') ||
        prop.startsWith('_react') ||
        prop === '_owner' ||
        prop === '_store') {
        propsToRemove.push(prop);
      }
    }
    propsToRemove.forEach(prop => {
      try {
        delete element[prop];
      } catch (e) {
        // Some properties might be non-configurable
      }
    });

    // Remove data-react attributes
    Array.from(element.attributes).forEach(attr => {
      if (attr.name.startsWith('data-react') ||
        attr.name.startsWith('__react')) {
        element.removeAttribute(attr.name);
      }
    });
  });

  return temp.innerHTML;
};

/**
 * Deep clean an object to remove non-serializable properties
 * @param {any} obj - Object to clean
 * @param {number} depth - Current recursion depth
 * @param {WeakSet} seen - Set of already visited objects
 * @returns {any} Clean object safe for JSON serialization
 */
export const sanitizeData = (obj, depth = 0, seen = new WeakSet()) => {
  // Prevent infinite recursion
  if (depth > 50) {
    console.warn('Data sanitization: Maximum depth reached');
    return null;
  }

  // Handle primitives
  if (obj === null || obj === undefined) return obj;
  if (typeof obj !== 'object') return obj;

  // Check for circular references
  if (seen.has(obj)) {
    return null; // Remove circular references
  }
  seen.add(obj);

  // Handle arrays
  if (Array.isArray(obj)) {
    return obj
      .map(item => sanitizeData(item, depth + 1, seen))
      .filter(item => item !== undefined);
  }

  // Handle dates
  if (obj instanceof Date) {
    return obj.toISOString();
  }

  // Handle DOM elements - don't store them
  if (obj instanceof Element || obj instanceof HTMLElement) {
    console.warn('Attempted to store DOM element in data layer');
    return null;
  }

  // Handle Window object
  if (obj === window || obj instanceof Window) {
    console.warn('Attempted to store Window object in data layer');
    return null;
  }

  // Handle Events
  if (obj instanceof Event) {
    console.warn('Attempted to store Event object in data layer');
    return null;
  }

  // Handle File objects - store metadata only
  if (obj instanceof File) {
    return {
      name: obj.name,
      type: obj.type,
      size: obj.size,
      lastModified: obj.lastModified
    };
  }

  // Handle React elements
  if (obj.$$typeof || obj._owner || obj._store) {
    console.warn('Attempted to store React element in data layer');
    return null;
  }

  // Handle plain objects
  const clean = {};
  for (const [key, value] of Object.entries(obj)) {
    // Skip React internal properties
    if (key.startsWith('__react') ||
      key.startsWith('_react') ||
      key === '_owner' ||
      key === '_store' ||
      key === '$$typeof' ||
      key === 'ref' ||
      key === 'key') {
      continue;
    }

    // Skip function properties
    if (typeof value === 'function') {
      continue;
    }

    // Skip DOM-related properties
    if (key === 'target' ||
      key === 'currentTarget' ||
      key === 'srcElement' ||
      key === 'view' ||
      key === 'nativeEvent') {
      continue;
    }

    // Recursively clean nested values
    const cleanValue = sanitizeData(value, depth + 1, seen);
    if (cleanValue !== undefined) {
      clean[key] = cleanValue;
    }
  }

  return clean;
};

/**
 * Sanitize message data before storage
 * @param {Object} message - Message object to sanitize
 * @returns {Object} Clean message object
 */
export const sanitizeMessage = (message) => {
  const cleaned = sanitizeData(message);

  // Ensure required message fields
  return {
    id: cleaned.id || `msg-${Date.now()}`,
    sender: cleaned.sender || 'user',
    timestamp: cleaned.timestamp || new Date().toISOString(),
    components: cleaned.components || [],
    ...cleaned
  };
};

/**
 * Sanitize and filter components array
 * @param {Array} components - Components array to sanitize
 * @returns {Array} Clean components array
 */
export const sanitizeComponentsArray = (components = []) => {
  if (!Array.isArray(components)) {
    return [];
  }
  return components
    .filter(Boolean)
    .map((component) => ({ ...component }));
};

// =============================================================================
// SECTION 3: ORIGINAL UTILS (Firebase, SSE, Serialization)
// =============================================================================

export const getFunctionUrl = (functionName) => buildFunctionUrl(functionName);

const CONNECTOR_WORKER_STATUS_LABELS = Object.freeze({
  starting: 'Preparing Daloopa',
  connecting: 'Connecting to Daloopa',
  retrieving: 'Retrieving licensed data',
  writing: 'Writing to the open file',
  verifying: 'Verifying the open file',
  completed: 'Daloopa work complete',
  failed: 'Daloopa request could not be completed',
  cancelled: 'Daloopa request cancelled',
});
const CONNECTOR_WORKER_STATUS_LABEL_SET = new Set(Object.values(CONNECTOR_WORKER_STATUS_LABELS));

export const isConnectorWorkerStatusLabel = (value) =>
  typeof value === 'string' && CONNECTOR_WORKER_STATUS_LABEL_SET.has(value.trim());

/**
 * Reduce a private connector-worker event to display-only status fields.
 * Provider results, query text, arguments, and backend messages never cross
 * this boundary into the normal chat state or its persistence paths.
 */
export const normalizeConnectorWorkerProgress = (event) => {
  if (event?.type !== 'connector_worker_progress') return null;
  if (event?.connectorId !== 'daloopa') return null;
  const connectorStage = typeof event?.stage === 'string' ? event.stage : '';
  const message = CONNECTOR_WORKER_STATUS_LABELS[connectorStage];
  if (!message) return null;
  return {
    type: 'status',
    stage: 'connector_worker',
    connectorId: 'daloopa',
    connectorStage,
    message,
  };
};

// Simple SSE event parser
export const parseSSEEvent = (jsonStr) => {
  try {
    // The jsonStr is already stripped of 'data: ' prefix
    const data = JSON.parse(jsonStr);
    return data;
  } catch (e) {
    console.error('Error parsing SSE event:', {
      name: e?.name || 'SyntaxError',
      inputLength: typeof jsonStr === 'string' ? jsonStr.length : 0,
    });
    return null;
  }
};

// Helper function to serialize components into text content
export const serializeComponentsToText = (components) => {
  if (!components || components.length === 0) return '';

  let serializedText = '';

  for (const component of components) {
    switch (component.type) {
      case 'thinking':
        // Include thinking content for context
        if (component.content) {
          serializedText += `\n[Thinking Process]\n${component.content}\n`;
        }
        break;

      case 'citations':
        // Include research sources
        if (component.data?.sources && component.data.sources.length > 0) {
          serializedText += `\n[Research Sources for "${component.data.query || 'query'}"]\n`;
          const sources = component.data.sources;
          for (let idx = 0; idx < sources.length; idx += 1) {
            const source = sources[idx];
            serializedText += `${idx + 1}. ${source.title} - ${source.url}\n`;
            if (source.snippet) {
              serializedText += `   "${source.snippet}"\n`;
            }
          }
        }
        break;

      case 'report':
        // Include full research report
        if (component.data?.content) {
          serializedText += `\n[Research Report]\n${component.data.content}\n`;
        }
        break;

      case 'text':
        // Include regular text
        if (component.content) {
          serializedText += `${component.content}\n`;
        }
        break;

      case 'slides':
        // Include slide information
        if (component.data && component.data.length > 0) {
          serializedText += `\n[Created ${component.data.length} slide(s)]\n`;
          const slidesData = component.data;
          for (let idx = 0; idx < slidesData.length; idx += 1) {
            const slide = slidesData[idx];
            serializedText += `Slide ${idx + 1}: ${slide.title || 'Untitled'}\n`;
          }
        }
        break;

      case 'editSlides':
        // Include slide edit information
        if (component.data) {
          serializedText += `\n[Edited slide: ${component.data.slideTitle || 'Untitled'}]\n`;
        }
        break;

      case 'attachments':
        // Include non-image attachment content inline
        if (component.data && component.data.length > 0) {
          const nonImageFiles = component.data.filter(f => f.contentType !== 'image');

          if (nonImageFiles.length > 0) {
            serializedText += '\n[Attached Files]\n';
            for (let idx = 0; idx < nonImageFiles.length; idx += 1) {
              const file = nonImageFiles[idx];
              serializedText += `\nFile ${idx + 1}: ${file.name}\n`;

              // Include content for PDFs, spreadsheets, and text files.
              // PDF and spreadsheet extractors already emit their own
              // bracketed heading line ("[PDF File: ...]" / "[Spreadsheet:
              // ...]"), so they're forwarded as-is. Plain text gets a
              // "Content:" prefix to disambiguate from the file label above.
              if (file.content) {
                if (file.contentType === 'pdf' || file.contentType === 'spreadsheet') {
                  serializedText += `${file.content}\n`;
                } else {
                  serializedText += `Content:\n${file.content}\n`;
                }
              } else if (file.error) {
                serializedText += `[Error: ${file.error}]\n`;
              }
            }
          }
        }
        break;

      case 'plan': {
        const outlineContent = component.data?.fullContent
          || component.data?.content
          || component.planContent
          || '';
        if (outlineContent.trim()) {
          serializedText += `\n[Plan Outline]\n${outlineContent.trim()}\n`;
        }
        break;
      }
      case 'planDecision': {
        const decisionMeta = component.decision || component.data?.decision || component.outlineData?.decision || {};
        const decisionType = decisionMeta.type || component.decisionType || 'planApproval';

        if (decisionType === 'clarification') {
          const promptText = decisionMeta.prompt || component.prompt || 'Clarification needed';
          serializedText += `\n[Clarification]\n${promptText.trim()}\n`;
          break;
        }

        const statusTokens = [];
        if (component.approved || component.outlineData?.approved || decisionMeta.approved) {
          statusTokens.push('approved');
        } else if (component.rejected || component.outlineData?.rejected || decisionMeta.rejected) {
          statusTokens.push('rejected');
        } else {
          statusTokens.push('awaiting approval');
        }
        const decisionText = component.planContent
          || component.fullPlanContent
          || component.outlineData?.fullContent
          || component.outlineData?.content
          || decisionMeta.planContent
          || decisionMeta.fullPlanContent
          || '';
        serializedText += `\n[Plan Decision ${statusTokens.join(' ')}]\n`;
        if (decisionText.trim()) {
          serializedText += `${decisionText.trim()}\n`;
        }
        break;
      }
      case 'clarification_response': {
        if (component.data?.response) {
          const promptLabel = component.data?.prompt ? ` (${component.data.prompt})` : '';
          serializedText += `\n[Clarification Response${promptLabel}] ${component.data.response}\n`;
        }
        break;
      }

      case 'createSlides':
      case 'rateLimit':
      case 'turnError':
      case 'status':
      case 'welcome_cards':
        // Skip UI-only components that shouldn't be serialized to text
        break;

      default:
        // Log unknown component types for debugging
        console.warn(`Unknown component type in serializeComponentsToText: ${component.type}`);
    }
  }

  return serializedText.trim();
};

// Format timestamp to display time
export const formatTime = (timestamp) => {
  if (!timestamp) return '';
  const date = new Date(timestamp);
  const hours = date.getHours();
  const minutes = date.getMinutes();
  const ampm = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours % 12 || 12;
  const displayMinutes = minutes < 10 ? `0${minutes}` : minutes;
  return `${displayHours}:${displayMinutes} ${ampm}`;
};

/**
 * Timestamp label for the chat-history sidebar. A bare time is only
 * meaningful for TODAY's chats; for anything older the DATE is the signal,
 * so it leads and the time follows:
 *   today            -> "2:34 PM"
 *   yesterday        -> "Yesterday · 2:34 PM"
 *   older, this year -> "Jul 8 · 2:34 PM"
 *   previous years   -> "Jul 8, 2025 · 2:34 PM"
 */
export const formatChatTimestamp = (timestamp) => {
  if (!timestamp) return '';
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return '';

  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startOfThatDay = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const dayDiff = Math.round((startOfToday - startOfThatDay) / 86400000);

  if (dayDiff <= 0) return formatTime(date);
  if (dayDiff === 1) return `Yesterday · ${formatTime(date)}`;

  const sameYear = date.getFullYear() === now.getFullYear();
  const dateLabel = date.toLocaleDateString(undefined, sameYear
    ? { month: 'short', day: 'numeric' }
    : { month: 'short', day: 'numeric', year: 'numeric' });
  return `${dateLabel} · ${formatTime(date)}`;
};

// Compact variant for single-line chat rows: time-of-day today, "Yest"
// yesterday, else "Jun 12" (with year only when it differs).
export const formatChatTimestampShort = (timestamp) => {
  if (!timestamp) return '';
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return '';

  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startOfThatDay = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const dayDiff = Math.round((startOfToday - startOfThatDay) / 86400000);

  if (dayDiff <= 0) return formatTime(date);
  if (dayDiff === 1) return 'Yest';

  const sameYear = date.getFullYear() === now.getFullYear();
  return date.toLocaleDateString(undefined, sameYear
    ? { month: 'short', day: 'numeric' }
    : { month: 'short', day: 'numeric', year: 'numeric' });
};

export const formatPlanStage = (stage) => {
  if (!stage) {
    return 'Update';
  }
  const map = {
    initialized: 'Queue Detected',
    model_iteration_start: 'Waiting on Model',
    reminder: 'Reminder Sent',
    reminder_retry: 'Reminder Reinforced',
    auto_invoke: 'Auto-Invoked Step',
    after_consume: 'Step Completed',
    plan_complete: 'Plan Complete'
  };
  return map[stage] || stage.replace(/_/g, ' ');
};

// =============================================================================
// SECTION 4: TODO UTILITIES
// =============================================================================

/**
 * Strip basic markdown formatting from text
 * @param {string} value - Text with markdown
 * @returns {string} Plain text
 */
const basicStripMarkdown = (value) => {
  if (typeof value !== 'string') {
    return '';
  }
  return value
    .replace(/^\s*[-*•]\s+/gm, '')
    .replace(/^\s*\[[xX\s]?\]\s+/gm, '')
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/__([^_]+)__/g, '$1')
    .replace(/\*(?!\s)([^*]+?)\*/g, '$1')
    .replace(/_(?!\s)([^_]+?)_/g, '$1')
    .replace(/`([^`]+)`/g, '$1')
    .trim();
};

/**
 * Normalize todo status to standard values
 * @param {string} status - Raw status value
 * @returns {string} Normalized status
 */
export const normalizeTodoStatus = (status) => {
  if (!status) {
    return 'pending';
  }

  const normalized = status.toString().trim().toLowerCase();
  if (['done', 'completed', 'complete', 'finished'].includes(normalized)) {
    return 'completed';
  }
  if (['in-progress', 'in progress', 'working', 'active', 'running'].includes(normalized)) {
    return 'in_progress';
  }
  if (['failed', 'blocked', 'error'].includes(normalized)) {
    return 'failed';
  }
  if (['skipped', 'suppressed', 'ignored', 'not_needed'].includes(normalized)) {
    return 'skipped';
  }
  return 'pending';
};

/**
 * Sanitize todo text by removing markdown
 * @param {string} value - Todo text with markdown
 * @returns {string} Clean todo text
 */
export const sanitizeTodoText = (value) => {
  if (!value) {
    return '';
  }

  return basicStripMarkdown(value);
};

/**
 * Normalizes and merges todo entries by toolCallId.
 * Consolidates SDK telemetry (tool_start, tool_complete) with todo entries.
 *
 * @param {Array} todos - Array of raw todo objects
 * @returns {Array} Normalized and deduplicated todo entries
 */
export const mapRawTodos = (todos = []) => {
  const seenContent = new Set();
  const toolMap = new Map(); // Map<toolCallId, todoEntry>

  // First pass: Build tool map and merge by toolCallId
  todos.filter(Boolean).forEach((todo, index) => {
    const toolCallId = todo.metadata?.toolCallId || todo.toolCallId;
    const rawContent = todo.content || todo.title || `Task ${index + 1}`;
    const content = sanitizeTodoText(rawContent);

    if (!content) {
      return;
    }

    const status = normalizeTodoStatus(todo.status);

    // If we have a toolCallId, merge with existing entry
    if (toolCallId) {
      const existing = toolMap.get(toolCallId);

      if (existing) {
        // Merge: Update status, timestamps, keep most recent data
        const mergedStatus = (() => {
          // Priority: failed > in_progress > completed > pending
          if (status === 'failed' || existing.status === 'failed') return 'failed';
          if (status === 'in_progress' || existing.status === 'in_progress') return 'in_progress';
          if (status === 'completed' && existing.status === 'completed') return 'completed';
          if (status === 'completed' || existing.status === 'completed') return 'completed';
          return status;
        })();

        toolMap.set(toolCallId, {
          ...existing,
          status: mergedStatus,
          isCompleted: mergedStatus === 'completed',
          isInProgress: mergedStatus === 'in_progress',
          startedAt: todo.startedAt || existing.startedAt || null,
          completedAt: todo.completedAt || existing.completedAt || null,
          updatedAt: todo.updatedAt || existing.updatedAt || null,
          metadata: {
            ...existing.metadata,
            ...todo.metadata,
            toolCallId
          }
        });
        return;
      }
    }

    // Create new entry
    const todoId = todo.id || toolCallId || `${todo.tool || 'step'}-${index + 1}`;

    const newEntry = {
      id: todoId,
      tool: todo.tool || null,
      content,
      rawContent,
      status,
      isCompleted: status === 'completed',
      isInProgress: status === 'in_progress',
      createdAt: todo.createdAt || null,
      startedAt: todo.startedAt || null,
      completedAt: todo.completedAt || null,
      updatedAt: todo.updatedAt || null,
      source: todo.source || todo.metadata?.source || null,
      description: todo.description || null,
      metadata: {
        ...todo.metadata,
        toolCallId
      }
    };

    if (toolCallId) {
      toolMap.set(toolCallId, newEntry);
    } else {
      // No toolCallId: deduplicate by content
      const contentKey = content.toLowerCase();
      if (!seenContent.has(contentKey)) {
        seenContent.add(contentKey);
        // Use a unique key to avoid conflicts
        toolMap.set(`content-${contentKey}`, newEntry);
      }
    }
  });

  // Convert map to array, assign order
  return Array.from(toolMap.values()).map((entry, index) => ({
    ...entry,
    order: index
  }));
};


// =============================================================================
// SECTION 5: CITATION PARSING
// =============================================================================

/**
 * Citation Parser Utility
 *
 * Parses research report text to extract inline citations [1] and their definitions.
 * Based on actual backend format from research-analyst subagent.
 *
 * Format Expected:
 * - Inline: [1], [2], [1][2] (multiple consecutive)
 * - Definitions section:
 *   ## SOURCE CITATIONS
 *   [1] Title - https://url.com
 *   [2] Another Title - https://url.com
 */

/**
 * Extracts citation definitions from the citations section
 * SIMPLIFIED: Just scan the entire text for [N] Title - URL pattern
 * @param {string} text - The full research report text
 * @returns {Map<number, {title: string, url: string}>} Map of citation numbers to citation data
 */
export function extractCitationDefinitions(text) {
  if (!text || typeof text !== 'string') {
    return new Map();
  }

  const citationMap = new Map();

  // SIMPLIFIED APPROACH: Scan entire text for citation pattern
  // Pattern: [number] text - http(s)://url
  // This handles ALL variations: with/without section headers, inline, etc.
  // Match [1] Title - https://url OR [1] "Title" - https://url OR [1] Title with - dashes - https://url
  const citationPattern = /\[(\d+)\]\s+(.+?)\s+-\s+(https?:\/\/[^\s<]+)/g;

  let match;
  while ((match = citationPattern.exec(text)) !== null) {
    const number = parseInt(match[1], 10);
    let title = match[2].trim();
    const url = match[3].trim();

    // Clean up title: remove quotes, extra whitespace
    title = title.replace(/^["']|["']$/g, '').trim();

    // Only add if we haven't seen this citation number yet (first occurrence wins)
    if (!citationMap.has(number)) {
      citationMap.set(number, { title, url });
    }
  }

  console.log(`📚 Extracted ${citationMap.size} citation definitions from entire text`);
  return citationMap;
}

/**
 * Extracts all inline citation numbers from text
 * @param {string} text - The text to search for inline citations
 * @returns {Set<number>} Set of unique citation numbers found
 */
export function extractInlineCitations(text) {
  if (!text || typeof text !== 'string') {
    return new Set();
  }

  const citationNumbers = new Set();

  // Pattern: [1] or [1][2][3] (consecutive citations)
  // Match all [number] patterns
  const inlinePattern = /\[(\d+)\]/g;

  let match;
  while ((match = inlinePattern.exec(text)) !== null) {
    const number = parseInt(match[1], 10);
    citationNumbers.add(number);
  }

  return citationNumbers;
}

/**
 * Removes the citation definitions section from text while keeping inline citations
 * SIMPLIFIED: Remove all lines that match [N] Title - URL pattern
 * @param {string} text - The full research report text
 * @returns {string} Text with citation definitions removed
 */
export function removeCitationDefinitions(text) {
  if (!text || typeof text !== 'string') {
    return text;
  }

  // First, try to find and remove a dedicated citations section
  const citationSectionPatterns = [
    /##\s*SOURCE CITATIONS[\s\S]*$/i,
    /##\s*CITATIONS[\s\S]*$/i,
    /##\s*SOURCES[\s\S]*$/i,
    /##\s*REFERENCES[\s\S]*$/i,
    /\*\*SOURCE CITATIONS:?\*\*[\s\S]*$/i,
    /\*\*CITATIONS:?\*\*[\s\S]*$/i,
    /\*\*SOURCES:?\*\*[\s\S]*$/i,
    /\*\*REFERENCES:?\*\*[\s\S]*$/i,
    /\*\*Source Citations:?\*\*[\s\S]*$/i
  ];

  let cleaned = text;
  for (const pattern of citationSectionPatterns) {
    const match = cleaned.match(pattern);
    if (match) {
      cleaned = cleaned.substring(0, match.index).trim();
      break;
    }
  }

  // Also remove any standalone citation definition lines: [1] Title - URL
  // This handles cases where citations are embedded inline
  cleaned = cleaned.replace(/^\[(\d+)\]\s+.+?\s+-\s+https?:\/\/[^\s<]+\s*$/gm, '');

  return cleaned.trim();
}

/**
 * Parses research report text to extract both content and citations
 * @param {string} text - The full research report text
 * @returns {{
 *   content: string,
 *   citations: Array<{number: number, title: string, url: string}>,
 *   inlineCitations: Set<number>
 * }}
 */
export function parseResearchReport(text) {
  if (!text || typeof text !== 'string') {
    return {
      content: text || '',
      citations: [],
      inlineCitations: new Set()
    };
  }

  // Extract citation definitions
  const citationMap = extractCitationDefinitions(text);

  // Extract inline citation numbers
  const inlineCitations = extractInlineCitations(text);

  // Remove citation definitions section
  const content = removeCitationDefinitions(text);

  // Convert citation map to array sorted by number
  const citations = Array.from(citationMap.entries())
    .map(([number, data]) => ({
      number,
      title: data.title,
      url: data.url
    }))
    .sort((a, b) => a.number - b.number);

  console.log(`📊 Parsed research report:`, {
    contentLength: content.length,
    citationsCount: citations.length,
    inlineCitationsCount: inlineCitations.size
  });

  return {
    content,
    citations,
    inlineCitations
  };
}

/**
 * Gets citation data by number
 * @param {number} citationNumber - The citation number to look up
 * @param {Map<number, {title: string, url: string}>} citationMap - The citation map
 * @returns {{title: string, url: string} | null} Citation data or null if not found
 */
export function getCitationByNumber(citationNumber, citationMap) {
  return citationMap.get(citationNumber) || null;
}

/**
 * Extracts the main title from the research report (first # or ## heading)
 * @param {string} text - The research report text
 * @returns {string | null} The extracted title or null
 */
export function extractReportTitle(text) {
  if (!text || typeof text !== 'string') {
    return null;
  }

  // Find the first # or ## heading (main title)
  const lines = text.split('\n');

  for (const line of lines) {
    const trimmedLine = line.trim();

    // Match # or ## headings (not more than 2 #'s for main title)
    const match = trimmedLine.match(/^#{1,2}\s+(.+)$/);
    if (match) {
      // Clean up the title - remove markdown formatting
      let title = match[1].trim();

      // Remove any **bold** markers
      title = title.replace(/\*\*(.+?)\*\*/g, '$1');

      // Remove any *italic* markers
      title = title.replace(/\*(.+?)\*/g, '$1');

      return title;
    }
  }

  return null;
}

/**
 * Normalize a web source URL into a comparison key.
 *
 * Used to dedupe the provenance list on the research card and to match a
 * fetched page against a search citation so the card can show a real title.
 * Only http(s) is accepted: these strings arrive from tool arguments and tool
 * results, which are model- and web-derived, so a `javascript:` or `data:`
 * URL must never become a rendered link.
 *
 * @param {string} url - Candidate URL
 * @returns {string|null} Lowercased origin+path key, or null when unusable
 */
export function normalizeSourceUrl(url) {
  if (!url || typeof url !== 'string') {
    return null;
  }

  const trimmed = url.trim();
  if (!/^https?:\/\//i.test(trimmed)) {
    return null;
  }

  try {
    const parsed = new URL(trimmed);
    // The fragment never changes which document was read, and a trailing
    // slash is the same page, so both are dropped from the key only.
    const host = parsed.host.toLowerCase().replace(/^www\./, '');
    const path = parsed.pathname.replace(/\/+$/, '');
    return `${parsed.protocol.toLowerCase()}//${host}${path}${parsed.search}`;
  } catch {
    return null;
  }
}

/**
 * Human-readable host for a source URL, or null when the URL is not usable.
 * @param {string} url - Candidate URL
 * @returns {string|null}
 */
export function getSourceDomain(url) {
  if (!normalizeSourceUrl(url)) {
    return null;
  }

  try {
    return new URL(url.trim()).hostname.replace(/^www\./, '');
  } catch {
    return null;
  }
}

// =============================================================================
// SECTION 6: FILE PROCESSING
// =============================================================================

/**
 * File processing utilities for extracting content from PDFs and images
 */

/**
 * Extract text content from a PDF file using PDF.js
 * @param {File} file - PDF file to process
 * @returns {Promise<string>} - Extracted text content
 */
export const extractTextFromPDF = async (file) => {
  try {
    // Try to dynamically import pdf.js
    const pdfjsLib = await import('pdfjs-dist/webpack').catch(() => null);

    if (!pdfjsLib) {
      console.warn('PDF.js not installed. Run "npm install pdfjs-dist" to enable PDF text extraction.');
      return `[PDF File: ${file.name} - Size: ${(file.size / 1024).toFixed(1)}KB]\n\nPDF text extraction is not available. To enable it, install pdf.js by running:\nnpm install pdfjs-dist`;
    }

    // Set worker source if not already set
    if (!pdfjsLib.GlobalWorkerOptions.workerSrc) {
      pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;
    }

    // Convert file to ArrayBuffer
    const arrayBuffer = await file.arrayBuffer();

    // Load the PDF document
    const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;

    let fullText = `[PDF File: ${file.name} - ${pdf.numPages} pages]\n\n`;

    // Extract text from each page
    for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
      const page = await pdf.getPage(pageNum);
      const textContent = await page.getTextContent();

      // Combine text items with proper spacing
      const pageText = textContent.items
        .map(item => item.str)
        .join(' ');

      if (pageText.trim()) {
        fullText += `--- Page ${pageNum} ---\n${pageText}\n\n`;
      }
    }

    return fullText.trim();
  } catch (error) {
    console.error('Error extracting text from PDF:', error);
    // Provide helpful error message
    return `[PDF File: ${file.name} - Error extracting text]\n\n${error.message}`;
  }
};

/**
 * Extract structured text from a spreadsheet (.xlsx/.xls/.csv) using SheetJS.
 *
 * Each sheet is rendered as a CSV block with a heading so the agent can
 * reason about the data with its normal text-handling capabilities (the
 * Agent SDK has no native spreadsheet parser; this mirrors the approach
 * we already use for PDFs). Multi-sheet workbooks produce multiple blocks
 * separated by blank lines.
 *
 * Cells with cached formula results show the value (not the formula) —
 * this is fine for "AI references the numbers" workflows. If you need
 * formula reasoning, switch to a server-side path with code execution.
 *
 * Output is capped at MAX_CHARS to protect the prompt size; oversize
 * sheets are truncated with a clear marker so the agent knows there's
 * more data and can ask the user for narrower input if needed.
 *
 * @param {File} file - .xlsx / .xls / .csv File
 * @returns {Promise<string>} - Extracted text content (CSV blocks)
 */
export const extractTextFromSpreadsheet = async (file) => {
  const MAX_CHARS = 200000; // ~50k tokens; large enough for most workbooks
  try {
    const XLSX = await import('xlsx').catch(() => null);
    if (!XLSX) {
      console.warn('xlsx package not installed. Run "npm install xlsx" to enable spreadsheet extraction.');
      return `[Spreadsheet: ${file.name} - Size: ${(file.size / 1024).toFixed(1)}KB]\n\nSpreadsheet text extraction is not available.`;
    }

    const buffer = await file.arrayBuffer();
    // cellDates: true preserves date formatting (otherwise dates come through
    // as raw Excel serial numbers, which the agent can't reason about).
    // cellNF: false omits number-format strings — the cached value is enough.
    const workbook = XLSX.read(buffer, { type: 'array', cellDates: true, cellNF: false });

    const sheetNames = Array.isArray(workbook.SheetNames) ? workbook.SheetNames : [];
    if (sheetNames.length === 0) {
      return `[Spreadsheet: ${file.name} - empty workbook]`;
    }

    const blocks = [];
    let totalChars = `[Spreadsheet: ${file.name} - ${sheetNames.length} sheet${sheetNames.length === 1 ? '' : 's'}]\n\n`.length;
    let truncated = false;

    for (const name of sheetNames) {
      const sheet = workbook.Sheets[name];
      if (!sheet) continue;
      // sheet_to_csv handles merged cells, blank rows, and number-formatted
      // cells correctly. blankrows: false drops empty rows so the prompt
      // doesn't fill up with whitespace.
      const csv = XLSX.utils.sheet_to_csv(sheet, { blankrows: false });
      const ref = sheet['!ref'] ? ` (${sheet['!ref']})` : '';
      const header = `--- Sheet: ${name}${ref} ---\n`;
      const remaining = MAX_CHARS - totalChars - header.length;
      if (remaining <= 0) {
        truncated = true;
        break;
      }
      let body = csv;
      if (body.length > remaining) {
        body = body.slice(0, remaining) + '\n…[sheet truncated]';
        truncated = true;
      }
      const block = header + body;
      blocks.push(block);
      totalChars += block.length + 2; // +2 for the join separator
      if (truncated) break;
    }

    const heading = `[Spreadsheet: ${file.name} - ${sheetNames.length} sheet${sheetNames.length === 1 ? '' : 's'}]\n\n`;
    const body = blocks.join('\n\n');
    const note = truncated
      ? `\n\n[Note: spreadsheet output truncated at ${MAX_CHARS.toLocaleString()} characters. Ask the user for a narrower file or specific sheets if you need more data.]`
      : '';
    return heading + body + note;
  } catch (error) {
    console.error('Error extracting text from spreadsheet:', error);
    return `[Spreadsheet: ${file.name} - Error extracting text]\n\n${error.message}`;
  }
};


/**
 * Process a file and prepare it for AI consumption
 * @param {File} file - File to process
 * @param {Object} options - Processing options
 * @param {string} options.presentationId - Presentation ID for storage upload
 * @param {string} options.messageId - Message ID for storage organization
 * @returns {Promise<Object>} - Processed file data with storage URL
 */
export const processFileForAI = async (file, options = {}) => {
  const fileData = {
    name: file.name,
    type: file.type,
    size: file.size,
    storageUrl: null,
    contentType: null,
    processingMethod: null,
    requiresUpload: false
  };

  try {
    if (file.type === 'application/pdf') {
      // PDF files will be uploaded to storage
      fileData.contentType = 'pdf';
      fileData.processingMethod = 'storage_for_tool_extraction';
      fileData.requiresUpload = true;
    } else if (file.type.startsWith('image/')) {
      // Image files will be uploaded to storage
      fileData.contentType = 'image';
      fileData.processingMethod = 'storage_for_tool_analysis';
      fileData.requiresUpload = true;
    } else if (file.type.startsWith('text/') ||
      file.type === 'application/json' ||
      file.type === 'application/javascript' ||
      file.name.endsWith('.txt')) {
      // For small text files, we can include content directly
      const text = await file.text();
      // Only include directly if small (< 10KB)
      if (text.length < 10240) {
        fileData.content = text;
        fileData.contentType = 'text';
        fileData.processingMethod = 'inline_text';
      } else {
        // Large text files go to storage
        fileData.contentType = 'text';
        fileData.processingMethod = 'storage_for_text';
        fileData.requiresUpload = true;
      }
    } else if (file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
      file.name.endsWith('.docx')) {
      // DOCX files will be uploaded to storage
      fileData.contentType = 'docx';
      fileData.processingMethod = 'storage_for_docx';
      fileData.requiresUpload = true;
    } else {
      // Other file types go to storage
      fileData.contentType = 'other';
      fileData.processingMethod = 'storage_generic';
      fileData.requiresUpload = true;
    }

    // Upload to storage if needed
    if (fileData.requiresUpload && options.presentationId && options.messageId) {
      const { uploadChatAttachment } = await import('../../../shared/services/storageService');
      const uploadResult = await uploadChatAttachment(
        options.presentationId,
        options.messageId,
        file,
        0
      );
      fileData.storageUrl = uploadResult.storageUrl;
      fileData.storagePath = uploadResult.storagePath;
    }
  } catch (error) {
    console.error(`Error processing file ${file.name}:`, error);
    fileData.content = `[Error processing ${file.name}: ${error.message}]`;
    fileData.contentType = 'error';
    fileData.processingMethod = 'error';
  }

  return fileData;
};

/**
 * Process multiple files for AI consumption
 * @param {File[]} files - Array of files to process
 * @param {Object} options - Processing options
 * @returns {Promise<Object[]>} - Array of processed file data
 */
export const processFilesForAI = async (files, options = {}) => {
  const processedFiles = await Promise.all(
    files.map((file, index) => processFileForAI(file, {
      ...options,
      fileIndex: index
    }))
  );

  return processedFiles;
};

// =============================================================================
// SECTION 7: FORMATTING
// =============================================================================

/**
 * Formatting Utilities
 * Consolidated from durationFormatting.js, tokenUtils.js, and timelineUtils.js
 */

// ============================================================================
// DURATION FORMATTING
// ============================================================================

/**
 * Format duration in seconds to human-readable label
 * @param {number} seconds - Duration in seconds
 * @returns {string} Formatted duration (e.g., "5s", "2m 30s", "1h 15m")
 */
export const formatDurationLabel = (seconds = 0) => {
  const safeSeconds = Number.isFinite(seconds) && seconds >= 0 ? Math.floor(seconds) : 0;
  if (safeSeconds < 60) {
    return `${safeSeconds}s`;
  }
  const minutes = Math.floor(safeSeconds / 60);
  const remainder = safeSeconds % 60;
  if (minutes >= 60) {
    const hours = Math.floor(minutes / 60);
    const minutesRemainder = minutes % 60;
    return `${hours}h${minutesRemainder > 0 ? ` ${minutesRemainder}m` : ''}${remainder > 0 ? ` ${remainder}s` : ''}`;
  }
  return `${minutes}m${remainder > 0 ? ` ${remainder}s` : ''}`;
};

// ============================================================================
// TOKEN UTILITIES
// ============================================================================

export const getRestackTokens = (theme) => {
  const themeTokens = theme?.restack?.tokens ?? {};
  const spacingTokens = themeTokens.spacing ?? {};
  const radiusTokens = themeTokens.radius ?? themeTokens.radii ?? {};
  const typographyTokens = themeTokens.typography ?? {};
  const surfacesTokens = themeTokens.surfaces ?? {};
  const bordersTokens = themeTokens.borders ?? {};

  const defaultSpacing = {
    nano: 0.5,
    quark: 0.75,
    xs: 1,
    sm: 1.5,
    md: 2,
    lg: 3,
    xl: 4
  };

  const defaultRadius = {
    xs: 4,
    sm: 8,
    md: 12,
    lg: 16,
    full: 9999
  };

  const defaultTypography = {
    bodySm: theme?.typography?.body2 ?? {
      fontSize: theme?.typography?.pxToRem
        ? theme.typography.pxToRem(14)
        : '0.875rem',
      lineHeight: 1.5
    }
  };

  return {
    spacing: { ...defaultSpacing, ...spacingTokens },
    radius: { ...defaultRadius, ...radiusTokens },
    typography: { ...defaultTypography, ...typographyTokens },
    surfaces: { ...surfacesTokens },
    borders: { ...bordersTokens }
  };
};

// ============================================================================
// TIMELINE UTILITIES
// ============================================================================

export const pickFirstString = (...values) => {
  for (const value of values) {
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (trimmed) {
        return trimmed.replace(/\s+/g, ' ');
      }
    }
  }
  return null;
};

export const formatTimelineLabel = (label) => {
  const resolved = pickFirstString(label);
  if (!resolved) {
    return null;
  }

  const normalized = resolved.replace(/[_-]+/g, ' ');
  const words = normalized.split(/\s+/).map((word) => {
    if (!word) {
      return '';
    }
    if (/^[A-Z]{2,}$/.test(word)) {
      return word;
    }
    const lower = word.toLowerCase();
    return lower.charAt(0).toUpperCase() + lower.slice(1);
  });

  return words.join(' ');
};

export const buildDetailText = (parts = [], fallback = null) => {
  const cleaned = parts
    .map((part) => (typeof part === 'string' ? part.trim() : ''))
    .filter(Boolean);
  if (cleaned.length > 0) {
    return cleaned.join(' • ');
  }
  return fallback;
};

export const deriveTimelineFlags = (component = {}, timelineType) => {
  const data = component?.data || {};
  const normalize = (value) => (typeof value === 'string' ? value.toLowerCase() : '');

  if (timelineType === 'loopTimeline') {
    const steps = Array.isArray(data?.steps) ? data.steps : [];
    const activeStep = steps.find((step) => {
      const status = normalize(step?.status);
      return status && status !== 'completed' && status !== 'abandoned' && status !== 'error';
    });
    const allCompleted = steps.length > 0 && steps.every((step) => normalize(step?.status) === 'completed');
    return {
      isActive: Boolean(activeStep),
      isComplete: allCompleted
    };
  }

  const isLoading = data?.isLoading === true || component?.isStreaming === true;
  const statusText = normalize(data?.status);
  const isCompleteByFlag = data?.isComplete === true;
  const completionKeywords = ['complete', 'completed', 'ready', 'added', 'created', 'updated', 'applied', 'finished'];
  const hasCompletionKeyword = completionKeywords.some((keyword) => statusText.includes(keyword));

  const isComplete = isCompleteByFlag || (hasCompletionKeyword && !isLoading);
  const isActive = Boolean(isLoading && !isComplete);

  return { isActive, isComplete };
};

// =============================================================================
// SECTION 8: SLIDE METADATA
// =============================================================================

/**
 * Utilities for working with slide metadata
 */

/**
 * Convert slide index (0-based) to slide number (1-based)
 * @param {number} index - 0-based slide index
 * @returns {number} 1-based slide number
 */
export const indexToNumber = (index) => (index ?? 0) + 1;

/**
 * Convert slide number (1-based) to slide index (0-based)
 * @param {number} number - 1-based slide number
 * @returns {number} 0-based slide index
 */
export const numberToIndex = (number) => Math.max(0, (number ?? 1) - 1);

/**
 * Create a normalized slide metadata object
 * @param {Object} slide - Raw slide data
 * @param {string} slide.id - Slide ID
 * @param {number} slide.index - Slide index (0-based)
 * @param {string} slide.title - Slide title
 * @param {Array} slide.changes - Array of changes (for edits)
 * @param {string} slide.contentPreview - Content preview
 * @param {boolean} slide.completed - Whether slide is completed
 * @param {boolean} slide.inProgress - Whether slide is in progress
 * @returns {Object} Normalized slide metadata
 */
export const createSlideMetadata = (slide) => ({
  id: slide.id,
  slideId: slide.id,
  slideIndex: slide.index ?? 0,
  slideNumber: indexToNumber(slide.index),
  title: slide.title || `Slide ${indexToNumber(slide.index)}`,
  changes: slide.changes || [],
  contentPreview: slide.contentPreview || null,
  completed: slide.completed ?? false,
  inProgress: slide.inProgress ?? false,
  isPlaceholder: slide.isPlaceholder ?? false
});

/**
 * Detect if slides represent an edit operation (vs creation)
 * Edits have changes with textBefore (showing what was replaced)
 * Creations only have textAfter (showing what was added)
 * @param {Array} slides - Array of slide objects
 * @returns {boolean} True if this is an edit operation
 */
export const isEditOperation = (slides) => {
  if (!Array.isArray(slides) || slides.length === 0) return false;

  return slides.some(slide =>
    Array.isArray(slide.changes) &&
    slide.changes.some(change =>
      change.textBefore &&
      change.textBefore.trim().length > 0
    )
  );
};

/**
 * Create a placeholder slide for progress tracking
 * @param {string} toolId - Tool ID for matching
 * @param {number} index - Slide index (0-based)
 * @returns {Object} Placeholder slide metadata
 */
export const createPlaceholderSlide = (toolId, index) => ({
  id: `placeholder-${toolId}-${index}`,
  slideIndex: index,
  slideNumber: indexToNumber(index),
  title: `Slide ${indexToNumber(index)}`,
  completed: false,
  inProgress: false,
  isPlaceholder: true
});

/**
 * Convert an array of slides to progress slides for UI display
 * @param {Array} slides - Array of slide objects
 * @param {number} totalSlides - Total expected slides
 * @returns {Array} Array of progress slide objects
 */
export const createProgressSlides = (slides, totalSlides) => {
  const progressSlides = [];

  for (let i = 0; i < totalSlides; i++) {
    const slide = slides.find(s => (s.index ?? s.slideIndex) === i);

    if (slide) {
      progressSlides.push(createSlideMetadata(slide));
    } else {
      progressSlides.push({
        completed: false,
        inProgress: false,
        slideIndex: i,
        slideNumber: indexToNumber(i)
      });
    }
  }

  return progressSlides;
};

// =============================================================================
// SECTION 9: OUTLINE HELPERS
// =============================================================================

/**
 * Filter out implementation blueprint section from plan content
 * @param {string} content - Plan content with potential blueprint
 * @returns {string} Filtered content without blueprint
 */
export const filterImplementationBlueprint = (content) => {
  if (!content) return '';

  const startMarker = '<!-- IMPLEMENTATION BLUEPRINT START -->';
  const endMarker = '<!-- IMPLEMENTATION BLUEPRINT END -->';

  const startIdx = content.indexOf(startMarker);
  const endIdx = content.indexOf(endMarker);

  if (startIdx !== -1 && endIdx !== -1 && endIdx > startIdx) {
    return content.substring(0, startIdx) + content.substring(endIdx + endMarker.length);
  }

  const blueprintSectionMatch = content.match(/2\.\s*IMPLEMENTATION BLUEPRINT[\s\S]*/i);
  if (blueprintSectionMatch) {
    return content.substring(0, blueprintSectionMatch.index).trim();
  }

  return content;
};

/**
 * Extract summary lines from outline content
 * @param {string} content - Outline content
 * @returns {Array<string>} Array of summary lines
 */
export const extractOutlineSummary = (content) => {
  if (!content) {
    return [];
  }

  const lines = content.split(/\r?\n/)
    .map(line => line.trim())
    .filter(Boolean);

  const summary = [];
  for (const line of lines) {
    if (/^(?:[-•*]|\d+\.|slide\s*\d+:)/i.test(line)) {
      const cleaned = line
        .replace(/^(?:[-•*]|\d+\.|slide\s*\d+:)\s*/i, '')
        .trim();
      if (cleaned) {
        summary.push(cleaned);
      }
    }
    if (summary.length >= 6) {
      break;
    }
  }

  if (summary.length === 0 && lines.length > 0) {
    summary.push(lines[0]);
  }

  return summary;
};

/**
 * Normalize outline status text
 * @param {string} status - Status text
 * @returns {string} Normalized status
 */
export const normalizeOutlineStatus = (status) => {
  if (!status) {
    return null;
  }
  return status.replace(/\bCompleted\b/gi, 'Complete');
};

/**
 * Build outline data object from plan content
 * @param {string} planContent - Plan content text
 * @param {Object} options - Additional options
 * @returns {Object} Outline data object
 */
export const buildOutlineData = (planContent, options = {}) => {
  const rawContent = planContent || '';
  const filteredOutline = filterImplementationBlueprint(rawContent);
  const summaryLines = extractOutlineSummary(filteredOutline);
  const detailLines = summaryLines.slice(1, 6);
  const actions = Array.isArray(options.actions) && options.actions.length > 0
    ? options.actions
    : summaryLines.slice(0, 8);
  const sections = Array.isArray(options.sections) ? options.sections : [];

  // IMPORTANT: Use raw content for display, not the filtered version
  // The filtered version is only for extracting summaries
  // This ensures the full plan content (including blueprints) is preserved
  return {
    fullContent: rawContent,  // Use raw content instead of filtered
    filteredContent: filteredOutline,  // Keep filtered version for reference
    headline: summaryLines[0] || 'Outline ready',
    detailLines,
    totalPoints: summaryLines.length,
    status: normalizeOutlineStatus(options.status || ''),
    prompt: options.prompt || null,
    showButtons: options.showButtons !== false,
    blueprint: options.blueprint || rawContent,  // Preserve blueprint
    actions,
    sections
  };
};

// =============================================================================
// SECTION 10: GUARD STATE
// =============================================================================

/**
 * Utilities for working with guard state (plan approval/rejection)
 */

/**
 * Guard status constants
 */
export const GUARD_STATUS = {
  IDLE: 'idle',
  BLOCKED: 'blocked',
  SATISFIED: 'satisfied',
  PENDING: 'pending'
};

/**
 * Create a normalized guard state object
 * Backend sends simplified {status, reason} - frontend normalizes to include derived flags
 * @param {Object} guardData - Raw guard data from backend
 * @param {string} guardData.status - Guard status ('idle', 'blocked', 'satisfied')
 * @param {string} guardData.reason - Reason for guard state
 * @param {string} guardData.lastStatusChange - Timestamp of last status change
 * @returns {Object} Normalized guard state
 */
export const createGuardState = (guardData = {}) => ({
  status: guardData.status || GUARD_STATUS.IDLE,
  satisfied: guardData.status === GUARD_STATUS.SATISFIED,
  blocked: guardData.status === GUARD_STATUS.BLOCKED,
  reason: guardData.reason || null,
  lastStatusChange: guardData.lastStatusChange || new Date().toISOString()
});

/**
 * Check if guard is in a blocking state
 * @param {Object} guard - Guard state object
 * @returns {boolean} True if guard is blocking
 */
export const isGuardBlocking = (guard) => {
  return guard?.status === GUARD_STATUS.BLOCKED || guard?.blocked === true;
};

/**
 * Check if guard is satisfied
 * @param {Object} guard - Guard state object
 * @returns {boolean} True if guard is satisfied
 */
export const isGuardSatisfied = (guard) => {
  return guard?.status === GUARD_STATUS.SATISFIED || guard?.satisfied === true;
};

/**
 * Merge guard state from response with existing state
 * @param {Object} responseGuard - Guard from backend response
 * @param {Object} existingGuard - Existing guard state
 * @returns {Object} Merged guard state
 */
export const mergeGuardState = (responseGuard, existingGuard) => {
  if (!responseGuard) return existingGuard;
  return createGuardState(responseGuard);
};

/**
 * Create initial guard state for a new session
 * @returns {Object} Initial guard state
 */
export const createInitialGuardState = () => createGuardState({
  status: GUARD_STATUS.IDLE,
  reason: null,
  lastStatusChange: null
});

// =============================================================================
// MODULE EXPORTS
// =============================================================================

export default {
  // Message Processing
  createMessage,
  shouldSaveMessage,
  isStreamComplete,
  createUserMessage,
  generateMessageId,
  augmentMessageComponents,

  // Data Sanitization
  sanitizeHTML,
  sanitizeData,
  sanitizeMessage,
  sanitizeComponentsArray,

  // Original Utils
  getFunctionUrl,
  parseSSEEvent,
  serializeComponentsToText,
  formatTime,
  formatChatTimestamp,
  formatPlanStage,

  // Todo Utilities
  normalizeTodoStatus,
  sanitizeTodoText,
  mapRawTodos,

  // Citation Parsing
  extractCitationDefinitions,
  extractInlineCitations,
  removeCitationDefinitions,
  parseResearchReport,
  getCitationByNumber,
  extractReportTitle,
  normalizeSourceUrl,
  getSourceDomain,

  // File Processing
  extractTextFromPDF,
  extractTextFromSpreadsheet,
  processFileForAI,
  processFilesForAI,

  // Formatting
  formatDurationLabel,
  getRestackTokens,
  pickFirstString,
  formatTimelineLabel,
  buildDetailText,
  deriveTimelineFlags,

  // Slide Metadata
  indexToNumber,
  numberToIndex,
  createSlideMetadata,
  isEditOperation,
  createPlaceholderSlide,
  createProgressSlides,

  // Outline Helpers
  filterImplementationBlueprint,
  extractOutlineSummary,
  normalizeOutlineStatus,
  buildOutlineData,

  // Guard State
  GUARD_STATUS,
  createGuardState,
  isGuardBlocking,
  isGuardSatisfied,
  mergeGuardState,
  createInitialGuardState
};

/**
 * Whether a connected Office (cross-app) tool call must pass the finance
 * document privacy guard first.
 *
 * Only when finance connectors are enabled. The guard stops a cross-app tool
 * touching a document a connector tainted with licensed data. With connectors
 * off nothing can taint a document, so the guard protects nothing. Running it
 * unconditionally broke every cross-app Excel and PowerPoint operation, because
 * the finance controller is null when the feature is off and the marker read
 * throws, surfacing as "Connected Office operation unavailable".
 */
export function shouldGuardRemoteToolCall(
  metadata,
  {
    financeConnectorsEnabled = process.env.REACT_APP_FINANCE_CONNECTORS_ENABLED === 'true',
  } = {}
) {
  return financeConnectorsEnabled === true &&
    metadata?.remote === true &&
    metadata?.private !== true;
}
